import { CookieKeys, getCookie, getRawCookie, setCookie } from '../hooks/useCookie';

const CLICK_ID_LIFETIME = 90 * 24 * 60 * 60 * 1000;
const META_PIXEL_FBP_COOKIE = '_fbp';

const CLICK_ID_PARAMS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'msclkid', 'rdt_cid'] as const;
type ClickIdParam = typeof CLICK_ID_PARAMS[number];
type ClickIdEntry = { value: string; ts: number };
type ClickIdStore = Partial<Record<ClickIdParam, ClickIdEntry>> & { fbp?: string };

const mergeIncomingClickIds = (store: ClickIdStore, search: string) => {
  const params = new URLSearchParams(search);
  return CLICK_ID_PARAMS.reduce<ClickIdStore>((next, param) => {
    const value = params.get(param);
    if (!value || next[param]?.value === value) {
      return next;
    }
    return { ...next, [param]: { value, ts: Date.now() } };
  }, store);
};

const randomFraction = () => {
  if (typeof crypto === 'undefined' || crypto.getRandomValues == null) {
    return Math.random();
  }
  return crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;
};

// Mirrors Meta's _fbp format: fb.<subdomainIndex>.<creationTime>.<randomNumber>
const generateFbp = () => `fb.1.${Date.now()}.${Math.floor(1e9 + randomFraction() * 9e9)}`;

const ensureFbp = (store: ClickIdStore) => {
  const pixelFbp = getRawCookie(META_PIXEL_FBP_COOKIE);
  if (pixelFbp) {
    return store.fbp === pixelFbp ? store : { ...store, fbp: pixelFbp };
  }
  return store.fbp != null ? store : { ...store, fbp: generateFbp() };
};

export const captureClickIds = (search: string) => {
  const store = getCookie<ClickIdStore>(CookieKeys.CLICK_IDS) ?? {};
  const next = ensureFbp(mergeIncomingClickIds(store, search));
  if (next === store) {
    return;
  }
  setCookie(CookieKeys.CLICK_IDS, next, CLICK_ID_LIFETIME);
};

// Meta's _fbc click-id format: fb.<subdomainIndex=1>.<capturedAtMs>.<fbclid>
const getFbc = () => {
  const metaFbc = getRawCookie('_fbc');
  if (metaFbc) {
    return metaFbc;
  }

  const fbclid = getCookie<ClickIdStore>(CookieKeys.CLICK_IDS)?.fbclid;
  return fbclid != null ? `fb.1.${fbclid.ts}.${fbclid.value}` : undefined;
};

type AttributionContext = { fbc?: string; fbp?: string } & Partial<
  Record<Exclude<ClickIdParam, 'fbclid'>, string>
>;

export const getAttributionContext = (): AttributionContext => {
  const fbc = getFbc();
  const store = getCookie<ClickIdStore>(CookieKeys.CLICK_IDS) ?? {};
  const fbp = getRawCookie(META_PIXEL_FBP_COOKIE) ?? store.fbp;
  const clickIds = CLICK_ID_PARAMS.reduce<AttributionContext>((context, param) => {
    const value = store[param]?.value;
    if (param === 'fbclid' || value == null) {
      return context;
    }
    return { ...context, [param]: value };
  }, {});

  return {
    ...(fbc ? { fbc } : {}),
    ...(fbp ? { fbp } : {}),
    ...clickIds,
  };
};
